// apps/pdf-suite/web/src/lib/licenseCheck.js
import { isDesktop } from './fileSystem';
import { setTier } from './license';
import {
  verifyStoredLicense,
  hasValidLicense,
  getLicenseClaims,
  getStoredLicenseToken,
  validateLicenseOnline,
} from './licenseToken';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.scrivar.com';

/**
 * Check account session + offline license on app launch (desktop only).
 *
 *   - Session present → ping /auth/heartbeat. 3 consecutive genuine failures → trial.
 *   - No session, valid license token → tier from the token (logged-out licensed
 *     device; the token was verified offline against this machine's fingerprint).
 *   - No session, no license → trial, {locked:false} (the gate handles routing).
 *   - Network error / HTTP 5xx → keep cached tier (offline grace, never a strike).
 *
 * Whenever a license token exists, also re-validate it online opportunistically:
 * a genuine 'revoked'/'transferred'/'invalid'/'expired' verdict clears it
 * immediately (deterministic DB states — and revoke must re-lock on the next
 * online validate). When logged out, that clears the only authed source →
 * {locked:true} so the caller re-renders and the gate redirects.
 */
export async function checkLicenseOnLaunch() {
  if (!isDesktop()) return;

  // Offline-capable license verification first — it is the tier source when
  // there's no session, and the gate's 4th authed source either way.
  await verifyStoredLicense();

  let session = null;
  try {
    session = await window.electronAPI.getSession?.();
  } catch {
    /* ignore */
  }
  if (!session) {
    try {
      const raw = localStorage.getItem('scrivar_session');
      session = raw ? JSON.parse(raw) : null;
    } catch {
      session = null;
    }
  }

  let result = { locked: false };

  if (!session?.token) {
    if (hasValidLicense()) {
      setTier(getLicenseClaims().tier);
    } else {
      setTier('trial');
    }
  } else {
    if (session.tier) setTier(session.tier);
    result = await heartbeatSession(session);
  }

  // Opportunistic online license re-validation (launch cadence, P2-5).
  if (getStoredLicenseToken()) {
    const v = await validateLicenseOnline();
    if (v.checked && !v.valid && !session?.token) {
      setTier('trial');
      return { locked: true, reason: `license_${v.reason}` };
    }
  }

  return result;
}

/**
 * The pre-existing session heartbeat flow, behavior unchanged: valid resets
 * the failure counter; a backend hiccup (HTTP 500 / reason 'server_error') is
 * NOT evidence the user's license is invalid — e.g. a transient DB-pool drop —
 * and never counts toward the lock-out strike counter; a GENUINE invalid
 * session (kicked or revoked) locks only after 3 strikes.
 */
async function heartbeatSession(session) {
  try {
    const res = await fetch(`${API_URL}/auth/heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionToken: session.token }),
    });
    const data = await res.json();

    if (data?.valid) {
      localStorage.setItem('sessionFailures', '0');
      if (data.tier && data.tier !== session.tier) {
        const updated = { ...session, tier: data.tier };
        localStorage.setItem('scrivar_session', JSON.stringify(updated));
        window.electronAPI.storeSession?.(updated).catch(() => {});
        setTier(data.tier);
      }
      return { locked: false };
    }

    if (!res.ok || data?.reason === 'server_error') {
      return { locked: false };
    }

    const failCount = parseInt(localStorage.getItem('sessionFailures') || '0') + 1;
    localStorage.setItem('sessionFailures', failCount.toString());
    if (failCount >= 3) {
      setTier('trial');
      await window.electronAPI.clearSession?.().catch(() => {});
      localStorage.removeItem('scrivar_session');
      localStorage.removeItem('sessionFailures');
      return { locked: true, reason: data?.reason || 'session_invalid' };
    }
    return { locked: false };
  } catch {
    // Offline — cached tier remains active.
    return { locked: false };
  }
}
